
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { useEffect } from 'react';

import { IBlogContent, IBlogTip } from '@common-types';
import { getBlogById } from '@store/slices';
import { RootState } from '@share/utils';
import { Loading } from '@share/components';
import { Routes } from '@share/constants';

import './style.scss';
import { isEmpty } from 'lodash';

export function Blog() {

  const { id } = useParams<{ id: string }>();

  const history = useHistory();

  const dispatch = useDispatch();

  const blogsStore = useSelector((state: RootState) => state.blogsStore);

  const { selectedBlog, loadingBlog, errorBlog } = blogsStore;

  useEffect(() => {
    // If no id, redirect to not authorized
    if (!id) {
      history.push(Routes.NotAuthorized);
      return;
    }

    // Parse id to number
    const blogId = parseInt(id, 10);

    // If id is not a valid number, redirect to not authorized
    if (isNaN(blogId)) {
      history.push(Routes.NotAuthorized);
      return;
    }

    // Fetch the blog by id
    dispatch(getBlogById(blogId));
  }, [id, history, dispatch]);

  useEffect(() => {
    if (errorBlog === '404') {
      history.push(Routes.NotFound);
    }
  }, [errorBlog]);

  // If no blog found (will redirect, but show something while redirecting)
  if (!selectedBlog && !loadingBlog) {
    return null;
  }

  return (
    <div className="blog-container">
      {loadingBlog && (
        <Loading />)}

      {selectedBlog && !loadingBlog && isEmpty(errorBlog) && (
        <>
          {selectedBlog.mainImage && (
            <img 
              src={selectedBlog.mainImage} 
              alt={selectedBlog.title}
              className="blog-main-image"
            />)}
          <h1><strong>{selectedBlog.title}</strong></h1>
          <div 
            className="blog-description"
            dangerouslySetInnerHTML={{ __html: selectedBlog.descriptions }}
          />
          {selectedBlog.contentList && selectedBlog.contentList.length > 0 && (
            <ul className="blog-content-list">
              {selectedBlog.contentList.map((item: string, itemIndex: number) => (
                <li key={itemIndex}><div dangerouslySetInnerHTML={{ __html: item }} /></li>
              ))}
            </ul>)}
          {selectedBlog.descriptionExtra && (
            <div 
              className="blog-description"
              dangerouslySetInnerHTML={{ __html: selectedBlog.descriptionExtra }}
            />)}

          {selectedBlog.content && selectedBlog.content.map((contentItem: IBlogContent, index: number) => (
            <div key={index} className="blog-content-item">
              <h3><strong>{contentItem.icon} <div dangerouslySetInnerHTML={{ __html: contentItem.title }} /></strong></h3>
              <div className="blog-content-body">
                <p className="blog-content-description"><div dangerouslySetInnerHTML={{ __html: contentItem.description }} /></p>
                {contentItem.contentList && contentItem.contentList.length > 0 && (
                  <ul className="blog-content-list">
                    {contentItem.contentList.map((item: string, itemIndex: number) => (
                      <li key={itemIndex}><div dangerouslySetInnerHTML={{ __html: item }} /></li>
                    ))}
                  </ul>
                )}
                {contentItem.tips && contentItem.tips.length > 0 && (
                  <div className="blog-tips">
                    {contentItem.tips.map((tip: IBlogTip, tipIndex: number) => (
                      <div key={tipIndex} className="blog-tip">
                        <strong>{tip.icon} {tip.title}</strong>
                        <div className="blog-tip-message">
                          {Array.isArray(tip.message) ? (
                            <ul>
                              {tip.message.map((msg: string, msgIndex: number) => (
                                <li key={msgIndex}><div dangerouslySetInnerHTML={{ __html: msg }} /></li>
                              ))}
                            </ul>
                          ) : (
                            <p>{tip.message}</p>
                          )}
                          {tip.image && (
                            <img 
                              src={tip.image} 
                              alt={tip.title}
                              className="blog-main-image"
                              style={{ width: '100%', height: 'auto', marginTop: '10px' }}
                            />)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          {selectedBlog.footerMessage && (
            <div className="blog-footer">
              ✨ <strong>{selectedBlog.footerMessage}</strong>
            </div>
          )}
        </>)}
    </div>
  );
}
